<template>
  <b-container fluid class="registration-form">
    <b-row v-for="(fieldPair, fieldPairIndex) in fieldPairs" :key="fieldPairIndex">
      <b-col :md="fieldPair[0].field === 'email' ? '' : '6'" class="field" v-for="(field, fieldIndex) in fieldPair" :key="fieldIndex">
        <b-form-group
          :id="`registration-field-${fieldIndex}`"
          class="text-left mb-1"
          >
          <b-form-input
            type="text"
            :id="`registration-field-input-${fieldIndex}`"
            :class="showValidationErrors(field.field) ? 'is-invalid' : ''"
            :name="field.field"
            :placeholder="fieldNames[field.field]"
            :required="field.required"
            v-model="fieldInputs[field.field]">
          </b-form-input>
          <div class="validationErrorMessage">&nbsp;<span v-if="showValidationErrors(field.field)">{{ validationErrorMessages[field.field] }}</span></div>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="questions">
      <b-col cols="12" class="question" v-for="question in questions" :key="question.questionKey">
        <b-form-group
          :id="`registration-question-${question.questionKey}`"
          class="text-left"
          :label="question.question"
          :label-for="`registration-question-input-${question.questionKey}`"
          >
          <b-form-input type="text" v-model="responses[question.questionKey]"
            :id="`registration-question-input-${question.questionKey}`"
            :name="question.questionKey">
          </b-form-input>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="button-row">
      <slot name="disclaimer"></slot> <!-- empty by default -->
      <b-button type="submit" :disabled="isDisabled" variant="primary" @click.prevent="clickSubmit">
        <!-- :style="{backgroundColor: brandingStyle.headerColor}"> -->
        {{$t('registration.form.button.register')}}
      </b-button>
    </b-row>

  </b-container>
</template>

<script>
import { localStorage } from '@/utils/storage';
import Vue from 'vue';
import * as EmailValidator from 'email-validator';

const RegistrationForm = {
  name: 'RegistrationForm',

  props: {
    fields: {
      type: Array,
      default: () => []
    },
    questions: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      rememberMe: localStorage.getItem('g2wRegistration.rememberMe') === 'true',
      fieldInputs: {},
      responses: [],
      submitted: false,
      isDisabled: false,
      fieldNames: {
        firstName: this.$t('registration.form.firstName'),
        lastName: this.$t('registration.form.lastName'),
        email: this.$t('registration.form.email')
      },
      validationErrorMessages: {
        firstName: this.$t('registration.form.validationErrors.firstName'),
        lastName: this.$t('registration.form.validationErrors.lastName'),
        email: this.$t('registration.form.validationErrors.email')
      }
    };
  },

  computed: {
    validator() {
      let _this = this;
      return {
        firstName(){
          return _this.fieldInputs['firstName'].length !== 0;
        },
        lastName(){
          return _this.fieldInputs['lastName'].length !== 0;
        },
        email(){
          return EmailValidator.validate(_this.fieldInputs['email']);
        }
      }
    },

    brandingStyle() {
      return this.$store.getters['g2wWebinar/brandingStyle'] || {};
    },

    fieldPairs() {
      const firstLastName = [];
      const email = [];
      const otherRequired = [];
      const other = [];

      this.fields.forEach((field) => {
        if (field.field === 'firstName') {
          firstLastName.unshift(field);
        } else if (field.field === 'lastName') {
          firstLastName.push(field);
        } else if (field.field === 'email') {
          email.unshift(field);
        } else if (field.required) {
          otherRequired.push(field);
        } else {
          other.push(field);
        }
      });

      const fieldPairs = [firstLastName];

      let pair = email;
      while (otherRequired.length || other.length) {
        const requiredField = otherRequired.shift();
        if (requiredField) {
          pair.push(requiredField);
        } else {
          const otherField = other.shift();
          if (otherField) {
            pair.push(otherField);
          }
        }

        if (pair.length === 2) {
          fieldPairs.push(pair);
          pair = [];
        }
      }

      if (pair.length) {
        fieldPairs.push(pair);
      }

      return fieldPairs;
    }
  },

  watch: {
    fields() {
      this.fieldInputs = {};
      this.refreshFields();
    }
  },

  created() {
    this.refreshFields();
    this.responses = this.questions.map((question) => ({ questionKey: question.questionKey, responseText: '' }));
  },

  methods: {
    showValidationErrors(fieldName){
      if (!this.submitted) {
        return false;
      } else {
        if (this.validator[fieldName]()) {
          return false;
        } else {
          return true;
        }
      }
    },

    fieldStorageKey(fieldName) {
      return `g2wRegistration.${fieldName}`;
    },

    refreshFields() {
      this.fields.forEach((field) => {
        const fieldName = field.field;
        const storageKey = this.fieldStorageKey(fieldName);
        Vue.set(this.fieldInputs, fieldName, localStorage.getItem(storageKey) || '')
      });
    },

    clickSubmit() {
      this.isDisabled = true;
      const { firstName: firstNameIsValid, lastName: lastNameIsValid, email: emailIsValid } = this.validator;
      if (!firstNameIsValid() || !lastNameIsValid() || !emailIsValid()) {
        this.submitted = true;
        this.isDisabled = false;
        return;
      }

      const submitData = {responses: this.responses};

      Object.keys(this.fieldInputs).forEach((key) => {
        submitData[key] = this.fieldInputs[key];
      });

      this.$emit('submit', submitData);

      /* if (this.rememberMe) */
      Object.keys(this.fieldInputs).forEach((fieldName) => {
        const storageKey = this.fieldStorageKey(fieldName);
        localStorage.setItem(storageKey, this.fieldInputs[fieldName]);
      });
      /*
        } else {
          Object.keys(this.fieldInputs).forEach((fieldName) => {
            const storageKey = this.fieldStorageKey(fieldName);
            localStorage.removeItem(storageKey);
          });
        }
        */
    }
  }
};

export default RegistrationForm;
</script>

<style lang="sass">
  @import 'src/scss/variables'
  $min-embed-width: 640px

  .registration-form
    width: 100%
    max-width: 900px
    font-weight: 300

    .btn
      border: none
      float: right

    .validationErrorMessage
      color: red
      font-size: 13px

    .row
      margin-left: auto
      margin-right: auto

    .button-row
      padding-left: 15px
      padding-right: 15px
      flex-direction: row
      flex-wrap: nowrap
      align-items: center

      @media (min-width: $min-embed-width+1)
        flex-direction: column
        align-items: flex-end



</style>
